import { Head, router } from "@inertiajs/react"
import { useMemo, useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface FlagRow {
  name: string
  description: string
  default: boolean
  override: boolean | null
  enabled: boolean
}
interface OrgOption {
  id: number
  name: string
  slug: string
}
interface Props {
  org_options: OrgOption[]
  selected_org: OrgOption | null
  flags: FlagRow[] | null
}

type FlagValue = "default" | "on" | "off"
type FilterKey = "all" | "overridden" | "on" | "off"

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Admin Console", href: "/admin/console" },
  { title: "Feature Flags", href: "/admin/feature_flags" },
]

function flagValue(f: FlagRow): FlagValue {
  if (f.override === null) return "default"
  return f.override ? "on" : "off"
}

export default function AdminFeatureFlags({
  org_options,
  selected_org,
  flags,
}: Props) {
  const [query, setQuery] = useState("")
  const [filter, setFilter] = useState<FilterKey>("all")

  const overriddenCount = useMemo(
    () => flags?.filter((f) => f.override !== null).length ?? 0,
    [flags],
  )
  const onCount = useMemo(
    () => flags?.filter((f) => f.enabled).length ?? 0,
    [flags],
  )

  const visible = useMemo(() => {
    if (!flags) return []
    const q = query.trim().toLowerCase()
    return flags.filter((f) => {
      if (
        q &&
        !f.name.includes(q) &&
        !f.description.toLowerCase().includes(q)
      ) {
        return false
      }
      if (filter === "overridden") return f.override !== null
      if (filter === "on") return f.enabled
      if (filter === "off") return !f.enabled
      return true
    })
  }, [flags, query, filter])

  function selectOrg(orgId: string) {
    router.get(
      "/admin/feature_flags",
      { org_id: orgId },
      { preserveScroll: true },
    )
  }

  function setFlag(name: string, value: FlagValue) {
    if (!selected_org) return
    router.post(
      "/admin/feature_flags",
      { org_id: selected_org.id, name, value },
      { preserveScroll: true, preserveState: true },
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Feature Flags · Admin" />
      <div className="space-y-6 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">Feature Flags</h1>
            <p className="text-muted-foreground text-sm">
              {flags
                ? `${flags.length} flags · ${onCount} on · ${overriddenCount} overridden. Changes apply immediately.`
                : "Per-org overrides on the global defaults. Changes apply immediately — no deploy needed."}
            </p>
          </div>

          <Select
            value={selected_org ? String(selected_org.id) : ""}
            onValueChange={selectOrg}
          >
            <SelectTrigger className="w-64">
              <SelectValue placeholder="Select an organization" />
            </SelectTrigger>
            <SelectContent>
              {org_options.map((o) => (
                <SelectItem key={o.id} value={String(o.id)}>
                  {o.name}{" "}
                  <span className="text-muted-foreground">({o.slug})</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {!flags && (
          <div className="text-muted-foreground rounded-md border border-dashed p-10 text-center text-sm">
            Select an organization to view and edit its feature flags.
          </div>
        )}

        {flags && (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search flags…"
                className="w-64"
              />
              <ToggleGroup
                type="single"
                variant="outline"
                size="sm"
                value={filter}
                onValueChange={(v) => v && setFilter(v as FilterKey)}
              >
                <ToggleGroupItem value="all">
                  All ({flags.length})
                </ToggleGroupItem>
                <ToggleGroupItem value="overridden">
                  Overridden ({overriddenCount})
                </ToggleGroupItem>
                <ToggleGroupItem value="on">On ({onCount})</ToggleGroupItem>
                <ToggleGroupItem value="off">
                  Off ({flags.length - onCount})
                </ToggleGroupItem>
              </ToggleGroup>
            </div>

            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Flag</TableHead>
                    <TableHead className="w-24">Effective</TableHead>
                    <TableHead className="w-72 text-right">Setting</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visible.map((f) => (
                    <TableRow
                      key={f.name}
                      className={
                        f.override !== null
                          ? "border-l-primary bg-primary/5 border-l-2"
                          : undefined
                      }
                    >
                      <TableCell className="py-3">
                        <div className="font-mono text-xs font-medium">
                          {f.name}
                        </div>
                        {f.description && (
                          <div className="text-muted-foreground mt-1 max-w-xl text-xs whitespace-normal">
                            {f.description}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {f.enabled ? (
                          <Badge>ON</Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="text-muted-foreground"
                          >
                            OFF
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <ToggleGroup
                          type="single"
                          variant="outline"
                          size="sm"
                          value={flagValue(f)}
                          onValueChange={(v) =>
                            v && setFlag(f.name, v as FlagValue)
                          }
                          aria-label={`Setting for ${f.name}`}
                          className="justify-end"
                        >
                          <ToggleGroupItem
                            value="default"
                            className="data-[state=on]:bg-muted-foreground/20 px-3 data-[state=on]:font-semibold"
                          >
                            Default · {f.default ? "on" : "off"}
                          </ToggleGroupItem>
                          <ToggleGroupItem
                            value="on"
                            className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground px-3"
                          >
                            On
                          </ToggleGroupItem>
                          <ToggleGroupItem
                            value="off"
                            className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground px-3"
                          >
                            Off
                          </ToggleGroupItem>
                        </ToggleGroup>
                      </TableCell>
                    </TableRow>
                  ))}
                  {visible.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        className="text-muted-foreground py-8 text-center text-sm"
                      >
                        No flags match.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  )
}
